import { composeValidators } from 'CorePackage/MainModule/validators/composeValidators';
import { makeNoLaterThenValidator } from 'CorePackage/MainModule/validators/isNoLaterThen';
import { makeNoSoonerThenValidator } from 'CorePackage/MainModule/validators/isNoSoonerThan';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { FormErrors } from 'KnmPackage/MainModule/types/FormErrors';
import { IKnmPmFormRescheduling } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormRescheduling';

import { IKnmErknmFormEvent } from '../interfaces/IKnmErknmFormEvent';
import { getSubmittedDocumentsValidate } from './getSubmittedDocumentsValidate';
import { getEventInspectorsValidate } from './getEventInspectorsValidate';
import { IKnmErknmFormRescheduling } from '../interfaces/IKnmErknmFormRescheduling';
import { IKnmErknmFormInspector } from '../interfaces/IKnmErknmFormInspector';
import { getKnmDataSigned } from '../../../KnmPackage/MainModule/selectors/knmSelectors';
import { store } from '../../../CorePackage/MainModule/common/store';
import { IKnmErknmFormSuspensionEvent } from '../interfaces/IKnmErknmFormSuspensionEvent';
import { isBooleanTrue } from '../../../CorePackage/MainModule/validators/isBooleanTrue';
import { checkIntersectionValidateForSuspensionEvents } from '../../../functions/checkIntersectionValidateForSuspensionEvents';

interface IGetErknmEventValidateProps {
  item: IKnmErknmFormEvent;
  isActEvent: boolean;
  holdingDate: string | undefined;
  rescheduling: (IKnmErknmFormRescheduling[] & IKnmPmFormRescheduling[]) | null | undefined;
  isInteractionCategoryOrInspectorRequired: boolean | undefined;
  inspectorsErknm: IKnmErknmFormInspector[] | undefined;
  isKnmStatusTypeCompleted: boolean | undefined;
  stopDate: string | undefined;
  startDate: string | undefined;
  isValidateActionStartDateInKnmAct: boolean;
  isShow22: boolean;
  isProsecutorOrSuperProsecutor: boolean | undefined;
  isKnmStatusApproving: boolean | undefined;
  suspensionEventsForActEvent?: IKnmErknmFormSuspensionEvent[];
  isPm?: boolean;
}

export const getErknmEventValidate = ({
  holdingDate,
  inspectorsErknm,
  isActEvent,
  isInteractionCategoryOrInspectorRequired,
  isKnmStatusApproving,
  isKnmStatusTypeCompleted,
  isProsecutorOrSuperProsecutor,
  isShow22,
  isValidateActionStartDateInKnmAct,
  item,
  rescheduling,
  startDate,
  stopDate,
  suspensionEventsForActEvent,
  isPm,
}: IGetErknmEventValidateProps): FormErrors<IKnmErknmFormEvent> => {
  const newRescheduling = rescheduling?.find(i => i.isNew);
  const state = store.getState();
  const isSigned = getKnmDataSigned(state);

  const isWithoutAction = item.type?.label === 'Осуществляется без действия';

  const { isValidStartDate, isValidStopDate } = checkIntersectionValidateForSuspensionEvents({
    item,
    suspensionEventsForActEvent,
  });

  return {
    type: makeRequired('Поле не может быть пустым')(item.type),
    interactionCategory:
      !isActEvent && isInteractionCategoryOrInspectorRequired
        ? makeRequired('Поле не может быть пустым')(item.interactionCategory)
        : undefined,
    inspectors:
      isActEvent && !isWithoutAction && inspectorsErknm && isInteractionCategoryOrInspectorRequired
        ? getEventInspectorsValidate(item.inspectors, isKnmStatusTypeCompleted)
        : undefined,
    startDate: composeValidators(
      makeNoLaterThenValidator('Дата начала не должна быть позже даты окончания', () => item.stopDate),
      makeRequired('Поле не может быть пустым', () => isWithoutAction),
      // eslint-disable-next-line no-nested-ternary
      !newRescheduling
        ? rescheduling?.length
          ? () => undefined
          : makeNoLaterThenValidator(
              'Дата начала действия не должна быть позже даты окончания проведения КНМ',
              () => stopDate,
            )
        : makeNoLaterThenValidator('Введите новую дату действия', () => newRescheduling.newEndDate),
      // eslint-disable-next-line no-nested-ternary
      !newRescheduling
        ? rescheduling?.length
          ? () => undefined
          : makeNoSoonerThenValidator(
              isActEvent
                ? 'Дата начала действия в акте должна быть больше или равна дате начала проведения КНМ в акте'
                : 'Дата начала действия не должна быть раньше даты начала проведения КНМ',
              () => (isActEvent && isValidateActionStartDateInKnmAct ? holdingDate : startDate),
            )
        : makeNoSoonerThenValidator('Введите новую дату действия', () => newRescheduling.newStartDate),
      () => isBooleanTrue('Дата начала действия должна учитывать даты приостановки')(isPm ? !isValidStartDate : false),
    )(item.startDate),
    stopDate: composeValidators(
      makeNoSoonerThenValidator('Дата окончания не должна быть раньше даты начала', () => item.startDate),
      makeRequired('Поле не может быть пустым', () => isWithoutAction),
      // eslint-disable-next-line no-nested-ternary
      !newRescheduling
        ? rescheduling?.length
          ? () => undefined
          : makeNoLaterThenValidator(
              'Дата окончания действия не должна быть позже даты окончания проведения КНМ',
              () => stopDate,
            )
        : makeNoLaterThenValidator('Введите новую дату действия', () => newRescheduling.newEndDate),
      // eslint-disable-next-line no-nested-ternary
      !newRescheduling
        ? rescheduling?.length
          ? () => undefined
          : makeNoSoonerThenValidator(
              isActEvent
                ? 'Дата окончания должна быть больше или равна дате начала осуществленного действия'
                : 'Дата окончания действия не должна быть раньше даты начала проведения КНМ',
              () => (isActEvent && isValidateActionStartDateInKnmAct ? holdingDate : startDate),
            )
        : makeNoSoonerThenValidator('Введите новую дату действия', () => newRescheduling.newStartDate),
      () =>
        isBooleanTrue('Дата окончания действия должна учитывать даты приостановки')(isPm ? !isValidStopDate : false),
    )(item.stopDate),
    submittedDocuments:
      isShow22 && !(isProsecutorOrSuperProsecutor && isKnmStatusApproving) && !isActEvent
        ? getSubmittedDocumentsValidate(item.submittedDocuments, item.type, isSigned, item.stopDate)
        : undefined,
  };
};
